import { isConfiguredFeederOriginAirportCode, rewriteOneWayCharterInclusions, rewriteCharterFeederInclusions } from 'lib/charterFlights/charterBundleOfferPageUtils'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import { useOfferInclusions } from 'hooks/useOfferInclusions'

const CHARTER_ROUTE_INFO = CHARTER_FLIGHT_DETAILS['melbourne-maldives']

interface Options {
  flightOrigin?: App.Airport
  hasFeederFlightIncluded: boolean
}

/**
 * Returns package inclusions with the selected feeder city reflected in a
 * charter flight inclusion, while preserving the standard inclusion groups.
 */
export default function usePackageUpgradeInclusions(pkg: App.Package, options: Options) {
  const { flightOrigin, hasFeederFlightIncluded } = options
  const intl = useIntl()
  const { oneWayBundle } = useOfferPageFlightPrice()
  const {
    inclusions: sourceInclusions,
    bonusInclusions,
    tacticalInclusions,
    luxPlusInclusions,
  } = useOfferInclusions(pkg)
  const inclusions = useMemo(() => {
    const { originAirport, destinationAirport } = CHARTER_ROUTE_INFO
    if (oneWayBundle) {
      return rewriteOneWayCharterInclusions(sourceInclusions, {
        feederCity: isConfiguredFeederOriginAirportCode('melbourne-maldives', flightOrigin?.code) ? flightOrigin?.name : undefined,
        originCity: originAirport.cityDesc,
        destinationCity: destinationAirport.cityDesc,
        intl,
      })
    }
    if (!hasFeederFlightIncluded || !flightOrigin) {
      return sourceInclusions
    }

    return rewriteCharterFeederInclusions(sourceInclusions, {
      feederCity: flightOrigin.name,
      originCity: originAirport.cityDesc,
      destinationCity: destinationAirport.cityDesc,
      intl,
    })
  }, [oneWayBundle, flightOrigin, hasFeederFlightIncluded, intl, sourceInclusions])

  return useMemo(() => ({
    inclusions,
    bonusInclusions,
    tacticalInclusions,
    luxPlusInclusions,
  }), [bonusInclusions, inclusions, luxPlusInclusions, tacticalInclusions])
}
